import { motion } from "framer-motion";
import { Music2, Disc3, Martini } from "lucide-react";
import { M, EASE } from "../lib/site";
import { MaskLines, FadeUp } from "./Reveal";

const Photo = ({ src, alt, className, testid, pos = "center" }) => (
  <motion.figure
    initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 1.2, ease: EASE }}
    className={`group relative overflow-hidden rounded-[28px] ${className}`} data-testid={testid}>
    <img src={src} alt={alt} loading="lazy" style={{ objectPosition: pos }}
      className="h-full w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.06]" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
  </motion.figure>
);

const Card = ({ icon: Icon, kicker, title, text, className, testid, delay }) => (
  <FadeUp delay={delay} className={`group relative flex flex-col justify-between overflow-hidden rounded-[28px] border border-[var(--line)] bg-[var(--card)] p-7 transition-[border-color] duration-500 hover:border-[rgba(229,184,105,0.5)] sm:p-9 ${className}`}>
    <div data-testid={testid} className="flex h-full flex-col justify-between gap-8">
      <div className="flex items-center justify-between">
        <span className="eyebrow">{kicker}</span>
        <Icon size={22} className="text-[var(--glow)] transition-transform duration-700 group-hover:rotate-[20deg]" />
      </div>
      <div>
        <h3 className="font-display text-4xl leading-none text-[#F8F6F0] sm:text-5xl">{title}</h3>
        <p className="mt-3 max-w-md text-[15px] leading-relaxed text-[var(--stone)]">{text}</p>
      </div>
    </div>
    <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[var(--glow)] opacity-0 blur-[90px] transition-opacity duration-700 group-hover:opacity-20" />
  </FadeUp>
);

export const Experience = () => (
  <section id="experience" className="relative mx-auto max-w-[1400px] px-5 py-24 sm:px-10 sm:py-36" data-testid="experience-section">
    <div className="mb-14 grid gap-8 lg:mb-20 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <p className="eyebrow mb-6">01 — The Night</p>
        <MaskLines lines={["Bottomless.", "Live DJs.", "Late nights."]}
          className="font-display text-5xl leading-[0.92] text-[#F8F6F0] sm:text-6xl lg:text-7xl" />
      </div>
      <FadeUp className="flex items-end lg:col-span-5" delay={0.2}>
        <p className="text-base leading-relaxed text-[var(--stone)] sm:text-lg">
          Fridays on Level 1, 412 Little Collins St. Mirror balls, low light, signature cocktails at the bar —
          and RNB, House and Afro House from 9PM til late.
        </p>
      </FadeUp>
    </div>

    <div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-6 md:auto-rows-[280px]">
      <Photo src={M.toast} alt="Guest raising a champagne glass at the Eleganté bar" testid="experience-photo-toast"
        pos="50% 25%" className="h-[460px] md:col-span-3 md:row-span-2 md:h-auto" />
      <Card icon={Music2} kicker="Early" title="RNB" testid="experience-card-rb" className="md:col-span-3"
        text="The soundtrack while the room fills up." />
      <Card icon={Disc3} kicker="Late" title="House & Afro House" testid="experience-card-afro" className="md:col-span-3" delay={0.1}
        text="The tempo lifts. The floor takes over." />
      <Card icon={Martini} kicker="All Night" title="Signature Cocktails" testid="experience-card-cocktails" className="md:col-span-2"
        text="Made for Friday. Easy to order twice." />
      <Photo src={M.heel} alt="Heel resting on a rose-gold mirror ball" testid="experience-photo-heel" pos="50% 40%" className="h-[380px] md:col-span-2 md:h-auto" />
      <FadeUp delay={0.15} className="md:col-span-2">
        <div className="flex h-full min-h-[240px] flex-col justify-between rounded-[28px] bg-[var(--glow)] p-7 text-[#09080B] sm:p-9" data-testid="experience-card-hours">
          <span className="font-mono-x text-xs uppercase tracking-[0.28em]">Doors</span>
          <div>
            <p className="font-display text-7xl leading-none sm:text-8xl">9PM</p>
            <p className="font-display text-3xl">Til late</p>
            <p className="mt-3 text-sm font-medium">Every Friday · Level 1 / 412 Little Collins St</p>
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
);
